import { config } from "../state/config";
import type { Agent, AgentSummary, ArkConnectionTest, ArkServiceConfig, AuthorizedDeviceAction, ClientApp, ClientKeyResult, ClientPlatform, Device, DeviceAction, DeviceActionExecution, Health, Invocation, MqttServiceConfig, RealtimeConnectionTest, RealtimeDeviceControlResult, RealtimeServiceConfig, RealtimeTicket, ServiceConnectionLog } from "../types";

type RealtimeServiceConfigInput =
  | { authMode: "API_KEY"; apiKey?: string }
  | { authMode: "APP_ID_ACCESS_TOKEN"; appId: string; accessKey?: string };

type AuthMode = "none" | "client" | "admin";
type ApiEnvelope<T> = { success: true; data: T } | { success: false; error: { code: string; message: string; details?: unknown }; traceId?: string };

export class ApiClientError extends Error {
  constructor(public code: string, message: string, public status: number, public traceId?: string) {
    super(message);
  }
}

function url(path: string): string {
  return `${config.baseUrl}${path}`;
}

function authHeaders(mode: AuthMode): Record<string, string> {
  if (mode === "client" && config.clientKey) return { "X-Client-Key": config.clientKey };
  if (mode === "admin" && config.adminKey) return { "X-Admin-Key": config.adminKey };
  return {};
}

async function request<T>(path: string, init: RequestInit = {}, mode: AuthMode = "client"): Promise<T> {
  const response = await fetch(url(path), {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(mode), ...init.headers },
  });
  const payload = await response.json() as ApiEnvelope<T>;
  if (!response.ok || !payload.success) {
    const failure = payload as Extract<ApiEnvelope<T>, { success: false }>;
    throw new ApiClientError(failure.error?.code ?? "REQUEST_FAILED", failure.error?.message ?? `HTTP ${response.status}`, response.status, failure.traceId);
  }
  return payload.data;
}

export const api = {
  health: () => request<Health>("/health/ready", {}, "none"),
  agents: () => request<AgentSummary[]>("/api/v1/agents"),
  adminAgents: () => request<Agent[]>("/api/admin/agents", {}, "admin"),
  clientApps: () => request<ClientApp[]>("/api/admin/client-apps", {}, "admin"),
  createClientApp: (input: { slug: string; name: string; platform: ClientPlatform; agentIds: string[] }) => request<ClientKeyResult>(
    "/api/admin/client-apps", { method: "POST", body: JSON.stringify(input) }, "admin",
  ),
  updateClientApp: (id: string, input: Partial<Pick<ClientApp, "slug" | "name" | "platform" | "isEnabled">>) => request<ClientApp>(
    `/api/admin/client-apps/${id}`, { method: "PATCH", body: JSON.stringify(input) }, "admin",
  ),
  rotateClientKey: (id: string) => request<ClientKeyResult>(
    `/api/admin/client-apps/${id}/rotate-key`, { method: "POST" }, "admin",
  ),
  invocations: (take = 50) => request<Invocation[]>(`/api/admin/invocations?take=${take}`, {}, "admin"),
  realtimeServiceConfig: () => request<RealtimeServiceConfig>("/api/admin/service-config/realtime", {}, "admin"),
  arkServiceConfig: () => request<ArkServiceConfig>("/api/admin/service-config/ark", {}, "admin"),
  saveArkServiceConfig: (input: { apiKey?: string; baseUrl: string }) => request<ArkServiceConfig>(
    "/api/admin/service-config/ark", { method: "PUT", body: JSON.stringify(input) }, "admin",
  ),
  testArkServiceConfig: (model?: string) => request<ArkConnectionTest>(
    "/api/admin/service-config/ark/test", { method: "POST", body: JSON.stringify({ ...(model ? { model } : {}) }) }, "admin",
  ),
  saveRealtimeServiceConfig: (input: RealtimeServiceConfigInput) => request<RealtimeServiceConfig>(
    "/api/admin/service-config/realtime", { method: "PUT", body: JSON.stringify(input) }, "admin",
  ),
  testRealtimeServiceConfig: () => request<RealtimeConnectionTest>(
    "/api/admin/service-config/realtime/test", { method: "POST" }, "admin",
  ),
  realtimeServiceConfigLogs: (take = 10) => request<ServiceConnectionLog[]>(
    `/api/admin/service-config/realtime/logs?take=${take}`, {}, "admin",
  ),
  mqttServiceConfig: () => request<MqttServiceConfig>("/api/admin/service-config/mqtt", {}, "admin"),
  saveMqttServiceConfig: (commandTimeoutMs: number) => request<MqttServiceConfig>(
    "/api/admin/service-config/mqtt", { method: "PUT", body: JSON.stringify({ commandTimeoutMs }) }, "admin",
  ),
  devices: () => request<Device[]>("/api/v1/devices"),
  adminDevices: () => request<Device[]>("/api/admin/devices", {}, "admin"),
  createDevice: (input: Pick<Device, "sceneKey" | "deviceCode"> & { name?: string | null }) => request<Device>(
    "/api/admin/devices", { method: "POST", body: JSON.stringify(input) }, "admin",
  ),
  updateDevice: (id: string, input: Partial<Pick<Device, "sceneKey" | "deviceCode" | "name">>) => request<Device>(
    `/api/admin/devices/${id}`, { method: "PATCH", body: JSON.stringify(input) }, "admin",
  ),
  adminDeviceActions: () => request<DeviceAction[]>("/api/admin/device-actions", {}, "admin"),
  saveDeviceAction: (action: Partial<DeviceAction> & Pick<DeviceAction, "name" | "actionKey" | "deviceId">) => action.id
    ? request<DeviceAction>(`/api/admin/device-actions/${action.id}`, { method: "PATCH", body: JSON.stringify(action) }, "admin")
    : request<DeviceAction>("/api/admin/device-actions", { method: "POST", body: JSON.stringify(action) }, "admin"),
  deleteDeviceAction: (id: string) => request<{ id: string }>(`/api/admin/device-actions/${id}`, { method: "DELETE" }, "admin"),
  saveAgent: (agent: Partial<Agent> & Pick<Agent, "name" | "slug">) => agent.id
    ? request<Agent>(`/api/admin/agents/${agent.id}`, { method: "PATCH", body: JSON.stringify(agent) }, "admin")
    : request<Agent>("/api/admin/agents", { method: "POST", body: JSON.stringify(agent) }, "admin"),
  setClientAgents: (clientId: string, agentIds: string[]) => request<{ clientAppId: string; agentIds: string[] }>(
    `/api/admin/client-apps/${clientId}/agents`, { method: "PUT", body: JSON.stringify({ agentIds }) }, "admin",
  ),
  setAgentDeviceActions: (agentId: string, actionIds: string[]) => request<{ agentId: string; actionIds: string[] }>(
    `/api/admin/agents/${agentId}/device-actions`, { method: "PUT", body: JSON.stringify({ actionIds }) }, "admin",
  ),
  sendCommand: (deviceId: string, fieldKey: string, value: string | number | boolean) => request(
    `/api/v1/devices/${deviceId}/commands`, { method: "POST", body: JSON.stringify({ fieldKey, value }) },
  ),
  realtimeTicket: (agent: string) => request<RealtimeTicket>(
    "/api/v1/realtime/tickets", { method: "POST", body: JSON.stringify({ agent }) },
  ),
  realtimeDeviceControl: (agent: string, text: string) => request<RealtimeDeviceControlResult>(
    "/api/v1/realtime/device-control", { method: "POST", body: JSON.stringify({ agent, text }) },
  ),
  deviceActions: (agent: string) => request<AuthorizedDeviceAction[]>(`/api/v1/agents/${encodeURIComponent(agent)}/device-actions`),
  executeDeviceAction: (agent: string, actionId: string) => request<DeviceActionExecution>(
    `/api/v1/device-actions/${actionId}/execute`, { method: "POST", body: JSON.stringify({ agent }) },
  ),
  async tts(agent: string, text: string): Promise<Blob> {
    const response = await fetch(url("/api/v1/tts"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders("client") },
      body: JSON.stringify({ agent, text }),
    });
    if (!response.ok) {
      const payload = await response.json() as Extract<ApiEnvelope<never>, { success: false }>;
      throw new ApiClientError(payload.error.code, payload.error.message, response.status, payload.traceId);
    }
    return response.blob();
  },
  async streamChat(input: {
    agent: string;
    messages: Array<{ role: "user" | "assistant"; content: string }>;
    includeDeviceContext: boolean;
    signal: AbortSignal;
    onEvent: (event: string, data: Record<string, unknown>) => void;
  }): Promise<void> {
    const response = await fetch(url("/api/v1/chat/stream"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders("client") },
      body: JSON.stringify({ agent: input.agent, messages: input.messages, includeDeviceContext: input.includeDeviceContext }),
      signal: input.signal,
    });
    if (!response.ok || !response.body) {
      const payload = await response.json() as Extract<ApiEnvelope<never>, { success: false }>;
      throw new ApiClientError(payload.error.code, payload.error.message, response.status, payload.traceId);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = block.split("\n").find((line) => line.startsWith("event:"))?.slice(6).trim() ?? "message";
        const data = block.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim()).join("\n");
        if (data) input.onEvent(event, JSON.parse(data) as Record<string, unknown>);
        boundary = buffer.indexOf("\n\n");
      }
      if (done) break;
    }
  },
};
